import { createApp, h } from 'vue';
import { createI18n } from 'vue-i18n';
import axiosFactory from 'axios';
import router from './router';
import store from './store';
import * as helpers from 'utils/helpers';
import App from 'components/app-root';
import Title from 'components/title';
import Loading from 'vue-loading-overlay';
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import focusDirective from './directives/focus';

// vee-validate v4 rules + message generator
import { defineRule, configure } from 'vee-validate';
import { required, min, max, regex, confirmed } from '@vee-validate/rules';
import { generateValidationMessage } from 'utils/helpers';

// Register rules
defineRule('required', required);
defineRule('min', min);
defineRule('max', max);
defineRule('regex', regex);
defineRule('confirmed', confirmed);

// i18n (XML resources already merged server-side into these keys)
const i18n = createI18n({
  legacy: true,
  locale: 'en',
  fallbackLocale: 'es',
  warnHtmlMessage: false,
  missingWarn: false,
  fallbackWarn: false
});

window.onerror = function (errorMsg) {
  console.error(errorMsg);
  router.push('/error');
  return false;
};

window.onunhandledrejection = function (event) {
  console.error(event.reason ? event.reason.message : null);
  router.push('/error');
  return false;
};

const appEl = document.querySelector('#app');
const appBase = appEl?.getAttribute('appbase') || '';
const hrefBase = appEl?.getAttribute('hrefbase') || '';
const displayMode = appEl?.getAttribute('displaymode') || '';

const axios = axiosFactory.create({ baseURL: hrefBase });
axios.interceptors.request.use(
  (config) => { store.commit('beginRequest'); return config; },
  (error) => Promise.reject(error)
);
axios.interceptors.response.use(
  (response) => { store.commit('endRequest'); return response; },
  (error) => { store.commit('endRequest'); return Promise.reject(error); }
);

// Initialize messages via helpers.setLocale to avoid runtime compilation
await helpers.setLocale.call({ $i18n: i18n.global, $hrefBase: hrefBase }, 'en');

const app = createApp(App);
app.mixin({ methods: { ...helpers } });

app.config.globalProperties.$http = axios;
app.config.globalProperties.$appBase = appBase;
app.config.globalProperties.$hrefBase = hrefBase;
app.config.globalProperties.$displayMode = displayMode;

// Build client messages from XML resources via i18n (CSP-safe: manual placeholder replacement)
configure({
  generateMessage: (ctx) => generateValidationMessage(i18n, ctx),
  validateOnBlur: false,
  validateOnChange: false,
  validateOnInput: true,
  validateOnModelUpdate: true
});

// Global error handler
app.config.errorHandler = (err, instance, info) => {
  console.error('[VueError]', info, err, 'instance:', instance?.$options?.name);
  router.push('/error');
};

app.directive('focus', focusDirective);

app.component('vue-title', Title);
app.component('alerts', {
  name: 'alerts',
  props: ['alertContext'],
  render() {
    return h('div', { class: 'alerts' });
  }
});
app.component('loading', Loading);

app.use(router);
app.use(store);
app.use(i18n);
app.use(FloatingVue);

export { app, router, store, i18n };
